import React, {forwardRef, useMemo, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useTable, usePagination, useSortBy, useRowSelect} from 'react-table'

import {Table, Pagination, Select, Checkbox} from 'components/ui'
import {capitalize} from 'utils/capitalize'

import TableRowSkeleton from './loaders/TableRowSkeleton'
import Loading from './Loading'
import TableTotalCount from './TableTotalCount'

const {Tr, Th, Td, THead, TBody, Sorter} = Table

const IndeterminateCheckbox = forwardRef(
    ({indeterminate, onChange, onCheckBoxChange, onIndeterminateCheckBoxChange, ...rest}, ref) => {
        const defaultRef = useRef()
        const resolvedRef = ref || defaultRef

        useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate
        }, [resolvedRef, indeterminate])

        const handleChange = (e) => {
            onChange(e)
            onCheckBoxChange?.(e)
            onIndeterminateCheckBoxChange?.(e)
        }

        return <Checkbox className="mb-0" ref={resolvedRef} onChange={(_, e) => handleChange(e)} {...rest} />
    },
)

const DataTable = ({
    skeletonAvatarColumns,
    columns = [],
    data = [],
    loading = false,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    onPaginationChange,
    onSelectChange,
    onSort,
    pageSizes = [],
    selectable = false,
    skeletonAvatarProps,
    pagingData = {
        total: 0,
        pageIndex: 1,
        pageSize: 10,
    },
    autoResetSelectedRows = true,
    onRowClick,
    hideTableCount = false,
    tableClassName,
}) => {
    const {pageSize, pageIndex, total} = pagingData

    const pageSizeOption = useMemo(
        () =>
            pageSizes.map((number) => ({
                value: number,
                label: `${number} / page`,
            })),
        [pageSizes],
    )

    const handleCheckBoxChange = (checked, row) => {
        if (!loading) {
            onCheckBoxChange?.(checked, row)
        }
    }

    const handleIndeterminateCheckBoxChange = (checked, rows) => {
        if (!loading) {
            onIndeterminateCheckBoxChange?.(checked, rows)
        }
    }

    const {getTableProps, getTableBodyProps, headerGroups, prepareRow, page} = useTable(
        {
            columns,
            data,
            manualPagination: true,
            manualSortBy: true,
            autoResetSelectedRows,
        },
        useSortBy,
        usePagination,
        useRowSelect,
        (hooks) => {
            if (selectable) {
                hooks.visibleColumns.push((columns) => [
                    {
                        id: 'selection',
                        Header: (props) => (
                            <div>
                                <IndeterminateCheckbox
                                    {...props.getToggleAllRowsSelectedProps()}
                                    onIndeterminateCheckBoxChange={(e) =>
                                        handleIndeterminateCheckBoxChange(e.target.checked, props.rows)
                                    }
                                />
                            </div>
                        ),
                        Cell: ({row}) => (
                            <div>
                                <IndeterminateCheckbox
                                    {...row.getToggleRowSelectedProps()}
                                    onCheckBoxChange={(e) => handleCheckBoxChange(e.target.checked, row.original)}
                                />
                            </div>
                        ),
                        sortable: false,
                    },
                    ...columns,
                ])
            }
        },
    )

    const handlePaginationChange = (page) => {
        if (!loading) {
            onPaginationChange?.(page)
        }
    }

    const handleSelectChange = (value) => {
        if (!loading) {
            onSelectChange?.(Number(value))
        }
    }

    const handleSort = (column) => {
        if (!loading) {
            const {id, isSortedDesc, toggleSortBy, clearSortBy} = column
            const sortOrder = isSortedDesc ? 'desc' : 'asc'
            toggleSortBy(!isSortedDesc)
            onSort?.({order: capitalize(sortOrder), key: capitalize(id)}, {id, clearSortBy})
        }
    }

    return (
        <Loading loading={loading && data.length !== 0} type="cover">
            <Table {...getTableProps()} className={tableClassName}>
                <THead>
                    {headerGroups.map((headerGroup) => {
                        const {key: headerGroupKey, ...headerGroupProps} = headerGroup.getHeaderGroupProps()

                        return (
                            <Tr key={headerGroupKey} {...headerGroupProps}>
                                {headerGroup.headers.map((column) => {
                                    const {key: columnKey, ...columnProps} = column.getHeaderProps()

                                    return (
                                        <Th key={columnKey} {...columnProps}>
                                            {column.render('Header') &&
                                                (column.sortable ? (
                                                    <div
                                                        className="flex items-center cursor-pointer"
                                                        onClick={() => handleSort(column)}
                                                    >
                                                        {column.render('Header')}
                                                        <Sorter sort={column.isSortedDesc} />
                                                    </div>
                                                ) : (
                                                    <div>{column.render('Header')}</div>
                                                ))}
                                        </Th>
                                    )
                                })}
                            </Tr>
                        )
                    })}
                </THead>
                {loading && data.length === 0 ? (
                    <TableRowSkeleton
                        columns={columns.length}
                        rows={pagingData.pageSize}
                        avatarInColumns={skeletonAvatarColumns}
                        avatarProps={skeletonAvatarProps}
                    />
                ) : (
                    <TBody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row)

                            const {key: rowKey, ...rowProps} = row.getRowProps()

                            return (
                                <Tr
                                    key={rowKey}
                                    {...rowProps}
                                    onClick={onRowClick?.bind(null, row)}
                                    className={!!onRowClick ? 'cursor-pointer' : ''}
                                >
                                    {row.cells.map((cell) => {
                                        const {key: cellKey, ...cellProps} = cell.getCellProps()

                                        return (
                                            <Td key={cellKey} {...cellProps} className={cell.column.className}>
                                                {cell.render('Cell')}
                                            </Td>
                                        )
                                    })}
                                </Tr>
                            )
                        })}
                    </TBody>
                )}
            </Table>
            <div className="flex items-center justify-between mt-4">
                <div style={{minWidth: 150}}>
                    {!hideTableCount && (
                        <TableTotalCount
                            loading={loading}
                            page={pageIndex}
                            pageSize={pageSize}
                            total={total}
                            dataLength={data.length}
                        />
                    )}

                    {pageSizes.length > 0 && (
                        <Select
                            size="sm"
                            menuPlacement="top"
                            isSearchable={false}
                            value={pageSizeOption.filter((option) => option.value === pageSize)}
                            options={pageSizeOption}
                            onChange={(option) => handleSelectChange(option.value)}
                        />
                    )}
                </div>
                <div>
                    {total / pageSize > 1 && (
                        <Pagination
                            pageSize={pageSize}
                            currentPage={pageIndex}
                            total={total}
                            onChange={handlePaginationChange}
                        />
                    )}
                </div>
            </div>
        </Loading>
    )
}

DataTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    onCheckBoxChange: PropTypes.func,
    onIndeterminateCheckBoxChange: PropTypes.func,
    onPaginationChange: PropTypes.func,
    onSelectChange: PropTypes.func,
    onSort: PropTypes.func,
    onRowClick: PropTypes.func,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
    selectable: PropTypes.bool,
    skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
    skeletonAvatarProps: PropTypes.object,
    pagingData: PropTypes.shape({
        total: PropTypes.number,
        pageIndex: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    autoResetSelectedRows: PropTypes.bool,
    hideTableCount: PropTypes.bool,
}

export default DataTable
